import dayjs from 'dayjs'

import { withTimezoneLabel } from './timezoneAbbreviation'

/**
 * Port of cdc-web `src/components/knockOuts/helpers/getScheduleDateLabel.ts`：
 * relative 分支（`getScheduleDateLabel`，TwoWayCard 用）+ absolute 分支
 * （`getAbsoluteScheduleDateLabel`，FutureCard 用——league 卡永远不显示
 * "Today"/星期，直接出日历日期，OG-8545）。`getScheduleDateParts` 不抄。
 */

export const isNotTheSameYear = (eventDate: string | number) => {
  const date = dayjs(eventDate)
  const currentYear = dayjs().year()
  const eventYear = date.year()
  return eventYear !== currentYear
}

/**
 * Returns a human-readable schedule label for a given event date.
 *
 * - Same calendar day: "Today, 8:30 pm"  (or "Today" if time not confirmed)
 * - < 7 calendar days away: "Wednesday, 8:30 pm"
 * - >= 7 calendar days away: "Jan 29, 8:30 pm" / "Jan 29, 2027 8:30 pm"
 *
 * Note: uses isSame('day') for "Today", NOT a 24h window.
 * e.g. Monday 11 PM → Tuesday 1 AM is 2h away but is NOT today.
 */
export function getScheduleDateLabel(eventDate: string, timeConfirmed: boolean): string {
  const eventDay = dayjs(eventDate)
  const timePart = timeConfirmed
    ? withTimezoneLabel(eventDay.format('h:mm a'), eventDay.toDate())
    : null

  let datePart: string
  if (eventDay.isSame(dayjs(), 'day')) {
    datePart = 'Today'
  } else if (eventDay.diff(dayjs(), 'day') < 7) {
    datePart = eventDay.format('dddd')
  } else {
    datePart = eventDay.format(isNotTheSameYear(eventDate) ? 'MMM D, YYYY' : 'MMM D')
  }

  if (!timePart) return datePart
  const separator = eventDay.diff(dayjs(), 'day') >= 7 && isNotTheSameYear(eventDate) ? ' ' : ', '
  return `${datePart}${separator}${timePart}`
}

/**
 * Absolute 变体：永远日历日期，同年 "Jun 29, 8:30 pm" / 跨年
 * "Jun 29, 2027 8:30 pm"。（cdc-web `getAbsoluteScheduleDateLabel` 1:1，
 * 底层走同一个 `formatEventDate` 形状。）
 */
export function getAbsoluteScheduleDateLabel(eventDate: string, timeConfirmed: boolean): string {
  const eventDay = dayjs(eventDate)
  const crossYear = isNotTheSameYear(eventDate)
  const format = crossYear ? 'MMM D, YYYY' : 'MMM D'
  const datePart = eventDay.format(format)
  if (!timeConfirmed) return datePart
  const timePart = withTimezoneLabel(eventDay.format('h:mm a'), eventDay.toDate())
  return `${datePart}${crossYear ? ' ' : ', '}${timePart}`
}
